import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  makeStyles,
  Card,
  CardContent,
  CardHeader,
  Radio,
  Typography,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Icon,
  Tooltip,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { blue, green, orange } from '@material-ui/core/colors';
import { StringUtils } from '../../../../../common/utils';
import { upgradeProduct } from '../details/SubscriptionChange';

type Color = 'green' | 'blue' | 'orange';

const colors = {
  green: green[500],
  blue: blue[400],
  orange: orange[500],
};

const useStyles = makeStyles(theme => ({
  card: {
    width: '750px',
  },
  border: {
    borderColor: (props: any) => colors[props.color as keyof typeof colors],
  },
  icon: {
    color: (props: any) => colors[props.color as keyof typeof colors],
  },
  header: {
    '& .MuiCardHeader-subheader': {
      opacity: '75%',
    },
  },
  content: {
    paddingTop: '0',
  },
  flex: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  lowercase: {
    textTransform: 'lowercase',
  },
  price: {
    marginRight: theme.spacing(1),
  },
  duration: {
    minWidth: '120px',
    marginRight: theme.spacing(1),
  },
  durationDays: {
    marginTop: theme.spacing(2),
  },
  expirationDate: {
    paddingTop: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
  credit: {
    marginLeft: theme.spacing(3),
  },
  live: {
    marginTop: theme.spacing(2),
  },
}));

type ProductCardProps = {
  productId: number;
  title: string;
  subtitle: string;
  icon: any;
  color: Color;
  price: number;
  duration: any;
  live: any;
  expirationDate: string;
  selected: boolean;
  onProductSelected: any;
  durationReadonly?: boolean;
  upgradeProduct?: upgradeProduct;
};

function ProductCard(props: ProductCardProps) {
  const [t] = useTranslation();
  const classes = useStyles(props);

  const {
    productId,
    title,
    subtitle,
    icon,
    color,
    price,
    duration,
    live,
    expirationDate,
    selected,
    onProductSelected,
    upgradeProduct,
    durationReadonly,
  } = props;

  return (
    <Card
      id={`ProductCard-${productId}`}
      variant="outlined"
      className={clsx(classes.card, {
        [classes.border]: color !== undefined,
      })}
    >
      <CardHeader
        className={classes.header}
        title={title}
        subheader={subtitle}
        avatar={
          <Icon
            className={clsx({
              [classes.icon]: color !== undefined,
            })}
          >
            {icon}
          </Icon>
        }
        action={
          <Radio
            id={`ProductCard-${productId}-radio`}
            inputProps={{ 'aria-label': `product-${productId}` }}
            value={productId}
            checked={selected}
            onChange={event => onProductSelected(event.target.value)}
          />
        }
      />
      <CardContent className={classes.content}>
        <Box className={classes.flex}>
          {!_.isNil(price) && (
            <Box id={`ProductCard-${productId}-price`} className={classes.flex}>
              <Typography variant="h5" className={classes.price}>
                {StringUtils.getFormattedSum(price)}
              </Typography>
              <Typography variant="body2" className={classes.lowercase}>
                {`/ ${t('common.month')}`}
              </Typography>
            </Box>
          )}
          {!_.isEmpty(duration) ? (
            <Box id={`ProductCard-${productId}-duration`} className={classes.flex}>
              <TextField
                id={`ProductCard-${productId}-durationSelect`}
                className={classes.duration}
                label={t('common.duration')}
                value={duration.value}
                onChange={event => duration.onChange(event.target.value)}
                disabled={!selected || durationReadonly}
                select
              >
                {duration.items.map((item: any) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" className={clsx(classes.lowercase, classes.durationDays)}>
                {t('common.days')}
              </Typography>
            </Box>
          ) : null}
          {!_.isEmpty(expirationDate) && (
            <Box id={`ProductCard-${productId}-expirationDate`} className={classes.expirationDate}>
              <Tooltip title={t('product.productExpires') ?? ''}>
                <Typography
                  id={`ProductCard-${productId}-expiration`}
                  variant="body2"
                  className={classes.flex}
                >
                  <Icon>hourglass_bottom</Icon>
                  &nbsp;
                  {expirationDate}
                </Typography>
              </Tooltip>
            </Box>
          )}
          {upgradeProduct && upgradeProduct?.credit && upgradeProduct?.credit > 0 ? (
            <Box id={`ProductCard-${productId}-credit`} className={classes.credit}>
              <Tooltip title={t('product.productCurrentPayment') ?? ''}>
                <Typography
                  id={`ProductCard-${productId}-credit`}
                  variant="h5"
                  className={classes.flex}
                >
                  <Icon
                    className={clsx({
                      [classes.icon]: 'green',
                    })}
                  >
                    credit_card
                  </Icon>
                  &nbsp;
                  {StringUtils.getFormattedSum(upgradeProduct.productPrice - upgradeProduct.credit)}
                </Typography>
              </Tooltip>
            </Box>
          ) : null}
          {upgradeProduct && upgradeProduct?.credit && upgradeProduct?.credit > 0 ? (
            <Box id={`ProductCard-${productId}-credit`} className={classes.credit}>
              <Tooltip title={t('product.productCredit') ?? ''}>
                <Typography
                  id={`ProductCard-${productId}-credit`}
                  variant="body2"
                  className={classes.flex}
                >
                  <Icon>savings</Icon>
                  &nbsp; -{StringUtils.getFormattedSum(upgradeProduct?.credit)}
                </Typography>
              </Tooltip>
            </Box>
          ) : null}
          {!_.isEmpty(live) && (
            <Grid id={`ProductCard-${productId}-live`} container className={classes.live}>
              {live.items.map((item: any, index: any) => {
                const liveChannels = [1237, 1261];
                const maxChannels = productId === 1231 ? 1 : 2;
                const checked = live.value.includes(item.id);
                const disabled = !selected || (!checked && live.value.length >= maxChannels) || liveChannels.indexOf(item.id) !== -1;
                return (
                  <Grid key={item.id} item xs={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id={`ProductCard-${productId}-liveChannel-${index}`}
                          color="primary"
                          value={item.id}
                          checked={checked}
                          onChange={event =>
                            live.onChange({
                              value: event.target.value,
                              checked: event.target.checked,
                            })
                          }
                          disabled={disabled}
                        />
                      }
                      label={item.title}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

ProductCard.defaultProps = {
  price: undefined,
  duration: undefined,
  live: undefined,
  expirationDate: undefined,
};

ProductCard.propTypes = {
  productId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['green', 'blue', 'orange']).isRequired,
  price: PropTypes.number,
  duration: PropTypes.shape({
    value: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.number).isRequired,
    onChange: PropTypes.func.isRequired,
  }),
  live: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.number).isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
  }),
  expirationDate: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  onProductSelected: PropTypes.func.isRequired,
  durationReadonly: PropTypes.bool,
};

export default ProductCard;
